import React, { useEffect, useState } from "react";
import { Button, Col, Form, Input, List, Row, Flex, Modal, Select, Grid, App } from "antd";
import { useRequerPermissoes } from "@/Hooks/useRequerPermissoes";

import { PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { TypeUsuario } from "@/Models/Usuario";
import { PerfilService, useBuscarPerfil, useBuscarUsuariosDoPerfil } from "@/Services/Sistema/PerfilService";
import { useMutation } from "@tanstack/react-query";
import { useForm } from "antd/es/form/Form";
import { PropriedadesDeNotificacao } from "@/Utils/Notificacoes";

import mitt from "mitt";
import { useListarTodosUsuarios } from "@/Services/UsuarioService";
const emitter = mitt<{ invokeSalvar: void; salvo: void }>();

type VinculoDePerfilProps = {
    codPerfil: number;
    esconderAcoes?: boolean;
}

const VinculoDePerfil: React.FC<VinculoDePerfilProps> = ({ codPerfil, esconderAcoes = false }) => {
    type Filtro = Parameters<typeof useListarTodosUsuarios>[0]["params"][0];

    const permissoes = useRequerPermissoes();
    const notification = App.useApp().notification;

    const [selecionados, setSelecionados] = useState<number[]>([]);
    const [filtro, setFiltro] = useState<Filtro>({});
    const [formFiltro] = useForm<Filtro>();

    const breakpoint = Grid.useBreakpoint();

    //#region controle do sistema
    const { perfil, query: perfilQuery } = useBuscarPerfil(codPerfil, codPerfil != undefined);
    //#endregion

    //#region pré-popular selecionados
    const { data: usuariosDoPerfilData, ...usuariosDoPerfilQuery } = useBuscarUsuariosDoPerfil({
        config: { enabled: codPerfil != undefined },
        params: [{ codPerfil }],
    });

    useEffect(() => {
        if(usuariosDoPerfilData) {
            setSelecionados(usuariosDoPerfilData.data);
        }
    }, [usuariosDoPerfilData]);
    //#endregion

    const { data: usuariosInfinitoData, ...usuariosInfinito } = useListarTodosUsuarios({ config: { enabled: true }, params: [filtro] });

    const usuarios = usuariosInfinitoData?.data;
    const dataSource = usuarios?.filter((u) => !selecionados.includes(u.codigo) );

    const adicionarUsuarioAoSelect = (usuario: TypeUsuario) => {
        if(!usuario.codigo) return;

        setSelecionados([...selecionados, usuario.codigo]);
    };

    const saveMutation = useMutation({
        mutationFn: () => {
            const usuarios = selecionados.map((s) => parseInt(s.toString()));

            const removidos = usuariosDoPerfilData?.data.filter((u) => !usuarios.includes(u)) ?? [];
            const adicionados = selecionados.filter((s) => !usuariosDoPerfilData?.data.includes(parseInt(s.toString())));

            return Promise.all([
                PerfilService.removerUsuarios(codPerfil, removidos),
                PerfilService.adicionarUsuarios(codPerfil, adicionados),
            ]);
        },
        onSuccess: () => {
            notification.success({...PropriedadesDeNotificacao.Sucesso, message: "Alterações salvas com sucesso.", duration: 1000});
            perfilQuery.refetch();
            usuariosDoPerfilQuery.refetch();

            emitter.emit("salvo");
        }
    });

    const salvar = () => {
        saveMutation.mutate();
    };

    const isLoading = perfilQuery.isFetching || usuariosInfinito.isFetching;

    useEffect(() => {
        emitter.on("invokeSalvar", salvar)

        return () => {
            emitter.off("invokeSalvar", salvar);
        }
    }, [])

    return (
        <Flex vertical gap={8}>
            <Form<Filtro> form={formFiltro} name="listar" layout="vertical" autoComplete="off" onFinish={(filtro) => setFiltro(filtro)}>
                <Row gutter={16}>
                    <Col xs={24} md={6}>
                        <Form.Item<Filtro> name="usuario" label="Nome de usuário">
                            <Input placeholder="Todos" />
                        </Form.Item>
                    </Col>

                    <Col xs={24} md={8}>
                        <Form.Item<Filtro> name="email" label="E-mail">
                            <Input placeholder="Todos" />
                        </Form.Item>
                    </Col>

                    <Col xs={24} md={8}>
                        <Form.Item<Filtro> name="nome" label="Nome">
                            <Input placeholder="Todos" />
                        </Form.Item>
                    </Col>

                    <Col xs={24} md={2}>
                        <Form.Item label={<></>}>
                            <Button style={{ width: "100%" }} type="primary" icon={<SearchOutlined />} htmlType="submit" />
                        </Form.Item>
                    </Col>
                </Row>
            </Form>

            <Form.Item label="Membros do perfil" labelCol={{ span: 24 }} wrapperCol={{ span: 24 }}>
                <Select
                    placeholder="Selecionados"
                    value={selecionados}
                    mode="multiple"
                    style={{ width: "100%" }}
                    onDeselect={(value) => setSelecionados(selecionados.filter((s) => s !== value))}
                >
                    {selecionados.map((codigo) => (
                        <Select.Option key={codigo} value={codigo}>
                            {usuarios?.find((u) => u.codigo === codigo)?.nome}
                        </Select.Option>
                    ))}
                </Select>
            </Form.Item>

            <Row gutter={16}>
                <Col xs={24}>
                    <div style={{ padding: 16 }} id="scrollable-permissoes">
                        <List
                            dataSource={dataSource}
                            header={<div className="common-list-header">Usuários</div>}
                            locale={{ emptyText: "Nenhum usuário encontrado." }}
                            bordered
                            loading={isLoading}
                            renderItem={(item) => (
                                <List.Item
                                    key={item.email}
                                    actions={[
                                        <Button type="primary" icon={<PlusOutlined />} onClick={() => adicionarUsuarioAoSelect(item)}>
                                            {breakpoint.md ? "Adicionar" : undefined}
                                        </Button>,
                                    ]}
                                >
                                    <List.Item.Meta title={item.nome} description={item.email} />
                                </List.Item>
                            )}
                        />
                    </div>
                </Col>
            </Row>

            {!esconderAcoes && (
                <Row style={{ display: "flex", justifyContent: "center", marginTop: 20 }}>
                    <Form.Item style={{ margin: 0 }}>
                        <Button
                            loading={saveMutation.isPending}
                            type={"primary"}
                            key="submit"
                            onClick={() => salvar()}
                            disabled={!permissoes.alguma("VincularPerfilAoUsuario")}
                        >
                            Salvar
                        </Button>
                    </Form.Item>
                </Row>
            )}
        </Flex>
    );
};

export const VinculoDePerfilModal: React.FC<{
    codPerfil: number;
    modalProps?: React.ComponentProps<typeof Modal>;
    buttonOpen?: React.ReactNode;
    onSuccess?: () => void;
}> = ({ codPerfil, buttonOpen, onSuccess, modalProps, ...props }) => {
    const [aberta, setAberta] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);

    useEffect(() => {
        const handleSalvo = () => {
            setLoading(false);
            setAberta(false);
            if (onSuccess) onSuccess();
        }

        emitter.on("salvo", handleSalvo);

        return () => {
            emitter.off("salvo", handleSalvo);
        }
    }, [])

    return (
        <>
            {(buttonOpen && React.cloneElement(buttonOpen as React.ReactElement<any>, { onClick: () => setAberta(true) })) || (
                <Button type="primary" size="small" onClick={() => setAberta(true)}>
                    Vincular Usuários
                </Button>
            )}

            <Modal
                destroyOnClose
                width={"80vw"}
                title="Vincular Usuários ao Perfil"
                maskClosable={false}
                open={aberta}
                {...modalProps}
                okButtonProps={{ loading }}
                onOk={() => {
                    setLoading(true);
                    emitter.emit("invokeSalvar");
                }}
                onCancel={() => {
                    setLoading(false);
                    setAberta(false);
                }}
            >
                <VinculoDePerfil codPerfil={codPerfil} esconderAcoes />
            </Modal>
        </>
    );
}; 